import { IncomingMessage } from 'http';
import { capitalize, filter, isNil, omitBy, startCase, trim } from 'lodash';
import {
  Icon,
  Icons,
  IconURL,
} from 'next/dist/lib/metadata/types/metadata-types';
import { CSSProperties } from 'react';

import { asText, ImageField, SliceZone } from '@prismicio/client';

import { ILocalAddress } from '@/components';
import {
  DESKTOP_MIN_WIDTH,
  FilterAttributeNameMapping,
  PRODUCT_FILTER_ATTRIBUTES,
  PROVIDER_GEOLOC_NOT_CONFIGURED,
  STATES_OF_RESIDENCE,
  TEMPLATE_BASE_PATH,
} from '@/constants';
import {
  AccountType,
  Address,
  AgeRestriction,
  BlogPostDocument,
  CartLineItem,
  Closure,
  CurbsideScheduleZone,
  EnumCustomerType as CustomerType,
  DayPeriod,
  Discount,
  DiscountRateType,
  DiscountType,
  EcomState,
  EnumCustomerType,
  GeoType,
  GProduct,
  IBlogTags,
  IPrismicSlice,
  JaneSpecial,
  JaneSpecialKind,
  LabResult,
  LineItemInput,
  Maybe,
  OpeningHoursSpecification,
  OrderAddress,
  OrderAddressInput,
  OrderDiscountItem,
  OrderDiscountItemInput,
  OrderInput,
  PageDocumentSlice,
  PickupScheduleZone,
  Product,
  PromoCarouselDocumentDataCarouselItemsItem,
  PromoCarouselDocumentDataCarouselItemsItemWithID,
  QueryFilters,
  ReservationMode,
  ScheduleWorkingHours,
  ScheduleZone,
  SortBySelectOption,
  Store,
  StoreAgreement,
  StorePaymentSettings,
  StorePaymentType,
  TreezFilters,
  TreezSpecials,
  TreezStore,
  Variant,
  WeekDay,
} from '@/types';

import { KeyTextField } from '@prismicio/client';
import { format, parse } from 'date-fns';
import { evaluate as evaluateTemplate } from '../scripts/utils';
import {
  DefaultDeliverySchedules,
  DefaultPickupSchedules,
} from './defaultSchedules';
import { isCurbsideAllowedForStore } from './featureFlags';
import { getSpecialLink } from './treez';
import { SortOptionalValue } from './defaultSort';
import { Compound } from '@/types';

export const currencyFormat = (value: number) => {
  if (!value) {
    return 0;
  }

  const toFloat = value / 100;

  return toFloat === Math.floor(toFloat)
    ? Math.floor(toFloat)
    : toFloat.toFixed(2);
};

export const formatProductDiscountText = ({
  value,
  format = 'percent',
  targetPrice = 0,
  discountMethod,
}: {
  value: Variant;
  format?: 'amount' | 'percent' | 'smart';
  targetPrice?: number;
  discountMethod?: string;
}): string | null => {
  if (!value?.discount_percent && !value?.discount_amount) return null;

  const price = Number(currencyFormat(value?.price as number));
  // Calculate the adjusted target price discount price - targetPriceDiscount
  const targetPriceDiscount = targetPrice ? price - targetPrice : 0;

  const formatByDiscountMethod =
    discountMethod === 'DOLLAR' ? 'amount' : 'percent';
  const discountFormat = format === 'smart' ? formatByDiscountMethod : format;

  // For amount format, subtract targetPriceDiscount directly from discount_amount
  if (discountFormat === 'amount') {
    const discountAmount = Number(
      currencyFormat(value?.discount_amount as number),
    );

    const totalDiscount = targetPriceDiscount
      ? discountAmount - targetPriceDiscount
      : discountAmount;

    const roundedDiscount = Math.round(totalDiscount * 100) / 100;

    // for cases when this discount is not stackable
    if (roundedDiscount === 0) return null;

    return `$${roundedDiscount} Off`;
  }

  // For percentage format, calculate the percentage that targetPriceDiscount represents
  if (value?.discount_percent) {
    let adjustedPercent = value.discount_percent;

    // If targetPriceDiscount exists and we have price info, adjust the percentage
    if (targetPriceDiscount > 0 && value?.price) {
      // Calculate what percentage targetPriceDiscount represents of the original price
      const targetPricePercentage = (targetPriceDiscount / price) * 100;
      // Subtract it from the original discount percentage
      adjustedPercent = value.discount_percent - targetPricePercentage;
    }

    // for cases when this discount is not stackable
    if (Math.round(adjustedPercent) === 0) return null;

    return `${Math.round(adjustedPercent)}% Off`;
  }

  return null;
};

export const addressToSlug = (text: string) => {
  if (!text) {
    return '';
  }

  return trim(text.replace(/ /g, '+').replace(/,/g, ''));
};

export const stringToSlug = (text: string | KeyTextField) => {
  if (!text) {
    return '';
  }

  return trim(
    text
      .toLowerCase()
      // eslint-disable-next-line no-useless-escape
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, ''),
    '-',
  );
};

export const stringToSlugBrands = (text: string) => {
  if (!text) {
    return '';
  }

  return trim(
    text
      .toLowerCase()
      // eslint-disable-next-line no-useless-escape
      .replace(/[^\w\s.-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, ''),
    '-',
  );
};

export const scrollToTop = () => {
  window.scrollTo(0, 0);
};

/**
 * Height occluded by the sticky account chrome. The views tab bar sits at
 * the bottom of the header → page-header → tabs stack, so its stuck bottom
 * (CSS `top` + height) already equals the full occluded height. Use it as
 * `scroll-margin-top` so native scrollIntoView lands the target just below
 * the sticky chrome. Returns 0 when absent (e.g. SSR).
 */
export const getAccountStickyOffset = (): number => {
  if (typeof document === 'undefined') return 0;

  const tabs = document.querySelector<HTMLElement>('.account_views');
  if (!tabs) return 0;

  const stickyTop = parseFloat(getComputedStyle(tabs).top);
  return (Number.isNaN(stickyTop) ? 0 : stickyTop) + tabs.offsetHeight;
};

/**
 * Scrolls to (and focuses) the first form control marked invalid (aria-invalid).
 * Returns false if none is found, so callers can fall back to scrollToTop.
 * Run it after the invalid state has rendered (e.g. a tick after setIsSubmited).
 */
export const scrollToFirstInvalidField = (): boolean => {
  if (typeof document === 'undefined') return false;

  const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (!el) return false;

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.focus?.({ preventScroll: true });

  return true;
};

export const getOperatingHours = () => {
  // Service hours. In this case from 8am to 11pm.
  return [8, 22];
};

export const personValidationRules = [
  {
    field: 'first_name',
    message: 'First name is required.',
    method: 'isEmpty',
    validWhen: false,
  },
  {
    field: 'last_name',
    message: 'Last name is required.',
    method: 'isEmpty',
    validWhen: false,
  },
  {
    field: 'address1',
    message: 'Invalid or incomplete address.',
    method: (_, state) => {
      return [
        'address1',
        'city',
        'country_code',
        'zip',
        'latitude',
        'longitude',
      ].every(field => state[field]);
    },
    validWhen: true,
  },
];

export const evaluate = <T>(val: T, elem): T => {
  return evaluateTemplate(val, elem);
};

export const formatWeightNumber = v => {
  return mapWeightLabelToGrams(v?.type);
};

const mapWeightLabelToGrams = (type: string) =>
  ({
    each: 0,
    eighth_ounce: 3.5,
    gram: 1,
    half_gram: 0.5,
    half_ounce: 14,
    ounce: 28,
    quarter_ounce: 7,
    two_gram: 2,
  }[type] ?? +type?.split('_')[0]);

export const formatNumber = number => {
  return Number(number).toLocaleString();
};

export const getServerUrl = ({ url, headers }: IncomingMessage) => {
  const protocol = headers.referer?.split('://')[0] || 'https';
  return `${protocol}://${headers.host}${url}`;
};

export const sanitizeObject = (object: Object) => {
  return omitBy(
    Object.keys(object || {}).reduce((acc, key) => {
      const currentValue = object[key];
      if (isNil(currentValue)) {
        return acc;
      }
      if (Array.isArray(currentValue)) {
        return {
          ...acc,
          [key]:
            typeof currentValue[0] === 'object'
              ? currentValue.map(sanitizeObject)
              : filter(currentValue, value => !isNil(value)),
        };
      }
      if (currentValue !== null && typeof currentValue === 'object') {
        return {
          ...acc,
          [key]: sanitizeObject(currentValue),
        };
      }
      return { ...acc, [key]: currentValue };
    }, {}),
    isNil,
  );
};

export const shouldHideNavigation = (path: string): boolean => {
  return ['/checkout', '/thank-you', '/checkin'].includes(path);
};

export const createLocationUrl = (path: string): string => {
  const location = TEMPLATE_BASE_PATH.LOCATION;

  return `${location}${path}`;
};

export const getRichTextText = (richText: any): string => {
  return richText?.[0]?.text || '';
};

const US_STATE_AND_ZIP = /\b([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\b/;

/**
 * Builds Schema.org PostalAddress fields from a Treez store.
 * Prefer structured `city` / `state`; parse street + ZIP from `full_address`
 * (e.g. "4591 Market St, Ventura, CA 93003").
 */
export const getTreezSeoAddress = (store: TreezStore): Address => {
  const parts = (store.full_address ?? '')
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  const streetAddress = parts[0] ?? '';
  const regionAndPostal = parts[parts.length - 1] ?? '';
  const regionAndPostalMatch = regionAndPostal.match(US_STATE_AND_ZIP);

  const addressLocality = trim(
    store.city ||
      (parts.length >= 3 ? parts[parts.length - 2] : parts[1]) ||
      '',
  );
  const addressRegion = trim(
    store.state || regionAndPostalMatch?.[1] || '',
  ).toUpperCase();
  const postalCode = regionAndPostalMatch?.[2] ?? '';

  return {
    addressCountry: 'US',
    addressLocality,
    addressRegion,
    postalCode,
    streetAddress,
  };
};

type seoHours = OpeningHoursSpecification | OpeningHoursSpecification[];

export const getTreezSeoOpeningHours = (store: TreezStore): seoHours => {
  if (!store?.delivery_hours?.length) {
    return [];
  }

  const specification: OpeningHoursSpecification[] = store.delivery_hours.map(
    (hours: DayPeriod): OpeningHoursSpecification => {
      return {
        closes: hours.period.to || '',
        dayOfWeek: hours.day,
        opens: hours.period.from || '',
      };
    },
  );

  return specification;
};

export const getFirstPTextAsDescrition = (richText: any): string => {
  let description: string =
    richText?.find(content => {
      return content.type === 'paragraph' && content.text;
    })?.text || '';

  if (description.length > 100) {
    description = description?.substring(0, 100).concat('...');
  }

  return description;
};

export const numberToShortString = (number: number): string => {
  if (number < 1000) {
    return String(number);
  }
  if (number < 1000000) {
    return `${(number / 1000).toFixed(1)}K`;
  }
  if (number < 1000000000) {
    return `${(number / 1000000).toFixed(1)}M`;
  }
  if (number < 1000000000000) {
    return `${(number / 1000000000).toFixed(1)}B`;
  }

  return undefined as unknown as string;
};

export const getBlogsTags = (blogs: BlogPostDocument<string>[]): IBlogTags => {
  const tags: IBlogTags = {};

  if (!blogs || !blogs.length) {
    return tags;
  }

  blogs.forEach(article => {
    if (article.tags.length === 0) {
      return;
    }

    article.tags.forEach(value => {
      if (!tags[value]) {
        tags[value] = { count: 1, slug: stringToSlug(value) };
      } else {
        tags[value].count += 1;
      }
    });
  });

  return tags;
};

export const getProductBundleSpecial = (
  specials: JaneSpecial[],
  product?: Product,
): JaneSpecial | undefined => {
  return specials
    .filter(j => j.special_type === 'bundle')
    .find(j => checkIfSpecialIsBundled(product!, j));
};

export const getBundledProducts = async (
  special: JaneSpecial,
  id: string,
): Promise<GProduct[]> => {
  // first filter and map bundled products
  const searchProducts = special?.bundle_products
    ?.filter(pr => pr !== id)
    ?.map(v => {
      return {
        product_id: v,
      };
    });

  if (!searchProducts?.length) {
    return [];
  }

  return [];

  // try {
  //     return []; //await getProductsByIdList(searchProducts);
  // } catch (error) {
  //     // eslint-disable-next-line no-console
  //     console.log(error);

  //     return [];
  // }
};

const checkIfSpecialIsBundled = (
  product: Product,
  special: JaneSpecial,
): boolean => {
  if (!product) {
    return false;
  }

  if (special.bundle_products?.length) {
    return special.bundle_products.find(b => b === product.id) !== undefined;
  }

  if (special.brands) {
    if (!special.brands.find(br => br === product.brand_name)) {
      return false;
    }
  }

  if (special.kinds) {
    return (
      special.kinds.find(kd => checkSpeciakKind(kd, product)) !== undefined
    );
  }

  return true;
};

const checkSpeciakKind = (kind: JaneSpecialKind, product: Product): boolean => {
  if (product.cannabis_type?.toLowerCase() !== kind.kind?.toLowerCase()) {
    return false;
  }

  if (kind.brand_subtype) {
    return kind.brand_subtype === product.brand_subtype;
  }

  return true;
};

export const formatEnUSPhoneNumber = (phone: string) => {
  if (!phone) {
    return '';
  }

  const match = phone
    ?.split('+1')
    ?.at(-1)
    ?.replace(/\D/g, '')
    ?.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);

  return !match?.[2]
    ? `+1 ${match?.[1]}`
    : '+1 (' + match[1] + ') ' + match[2] + (match[3] ? '-' + match[3] : '');
};

export function toQueryFilters(input: TreezFilters): QueryFilters {
  const queryFilters: QueryFilters = {};

  for (const attribute in input) {
    if (input.hasOwnProperty(attribute)) {
      const values = input[attribute].values ?? input[attribute]?.value;
      queryFilters[attribute] = {};

      if (Array.isArray(values)) {
        values.forEach(value => {
          queryFilters[attribute][value] = true;
        });
      } else if (typeof values === 'string') {
        queryFilters[attribute][values] = true;
      }
    }
  }

  return queryFilters;
}

export function toQuerySortBy(selectOption: SortBySelectOption): string {
  if (selectOption.value === 'default') {
    return undefined as unknown as string;
  }

  return `${selectOption.value.field}${startCase(selectOption.value.order)}`;
}

const getVariantPrice = (salePrice: number, price: number): number => {
  if (!salePrice) {
    return price;
  }

  if (salePrice > price) {
    return price;
  }

  return salePrice;
};

const getAccountType = (customerType: CustomerType) =>
  ({
    [CustomerType.ADULT]: AccountType.Adult,
    [CustomerType.MEDICAL]: AccountType.Medical,
  }[customerType] ?? AccountType.Adult);

const CURBSIDE_METADATA_KEY = 'IS_CURBSIDE_ENABLED_ON_POS';

// this function is used to add the metadata to the order depending on the store curbside flag
export const getStoreOrderMetadata = (
  store?: TreezStore | null,
): Pick<OrderInput, 'metadata'> | undefined =>
  // when the store has curbside enabled, we will add the related key and value to the order
  store?.curbside === true
    ? { metadata: [{ key: CURBSIDE_METADATA_KEY, value: '1' }] }
    : undefined;

export const getOrderFromEcomState = (
  state: EcomState,
  store: TreezStore,
): OrderInput => {
  return {
    entity_id: state.entity_id,
    email: state.email,
    date_of_birth: state.date_of_birth,

    // payment
    payment_details: state.payment_details,

    // payment address
    delivery_address: state.delivery_address,

    // delivery details
    delivery_details: state.delivery_details,

    // discount items
    discount_items: state.discount_items.map(
      (i: OrderDiscountItem): OrderDiscountItemInput => ({
        blaze_id: i.blaze_id,
        discountType: i.discountType as DiscountType,
        discount_apply_type: i.discount_apply_type,
        discount_rate_type: i.discount_rate_type as DiscountRateType,
        id: i.id as string,
        promo_code: i.promo_code,
        rate: i.rate as number,
      }),
    ),

    // line items
    line_items: mapLineItems(state.line_items),

    notes: state.notes,
    buyer_accepts_marketing: state.buyer_accepts_marketing,

    // provider info
    provider_store_full_address: store?.full_address,
    provider_store_id: store?.short_name,
    provider_store_name: store?.name,
    provider_store_phone_number: store?.phone,

    // totals
    subtotal_price: state.subtotal_price,
    total_delivery: state.total_delivery,
    total_discounts: state.total_discounts,
    total_line_items_price: state.total_line_items_price,
    total_price: state.total_price,
    total_service_fee: state.total_service_fee,
    total_tax: state.total_tax,

    // medical info
    order_type: getAccountType(state.customer_type),
    order_medical_id_expiration_date: '',
    order_medical_id: '',

    // alpine iq
    alpineiq_template_id: state.alpineiq_template_id,
    alpineiq_redemption_url: state.alpineiq_redemption_url,

    // sticky cards
    sticky_template_id: state.sticky_template_id,

    //treez order
    treez_order_id: state.treez_order_id,
    treez_order_number: state.treez_order_number,

    treez_pay_ticket_ids_by_store: state.treez_pay_ticket_ids_by_store,

    //state of residence
    state_of_residence: state.state_of_residence,

    //allow custom delivery fee
    allow_custom_delivery_fee: state.allow_custom_delivery_fee,
  };
};

export const getProductUrl = (url?: string | null): string | undefined => {
  if (!url) return undefined;

  const domain = process.env.NEXT_PUBLIC_STORE_DOMAIN ?? '';
  const proxyPrefix = process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? '';

  return `${domain}${proxyPrefix}${url}`;
};

export const mapLineItems = (lineItems: CartLineItem[]): LineItemInput[] => {
  if (!lineItems?.length) {
    return [];
  }

  return lineItems.map(
    (lineItem: CartLineItem): LineItemInput => ({
      brand: lineItem.brand_name,
      category: lineItem.category,
      id: lineItem?.id?.toString(),
      item_id: lineItem?.id?.toString(),
      pos_id: lineItem?.id?.toString(),
      base_weight: lineItem?.base_weight,
      tier_method: lineItem?.tier_method,
      name: lineItem.name,
      price: lineItem.weightVariant?.price,
      quantity: lineItem.quantity,
      sale_price: lineItem.weightVariant
        ? lineItem.weightVariant?.salePrice
        : lineItem.sales_price,
      thumbnail: lineItem.asset?.image,
      price_type: lineItem.price_type,
      product_url: getProductUrl(lineItem.url),
      variants: [
        {
          id: lineItem?.weightVariant?.id?.toString(),
          name: lineItem.weightVariant?.name,
          price: getVariantPrice(
            lineItem?.weightVariant?.salePrice as number,
            lineItem?.weightVariant?.price as number,
          ),
          type: lineItem.weightVariant?.type,
        },
      ],
    }),
  );
};

export function encryptText(text) {
  return text;
  // const key = process.env.NEXT_PUBLIC_ENCRYPTION_KEY;
  // const textEncryption = CryptoJS.AES.encrypt(text, key).toString();

  // return textEncryption;
}

export function decryptText(textEncrypt) {
  return textEncrypt;

  // const key = process.env.NEXT_PUBLIC_ENCRYPTION_KEY;
  // const bytes = CryptoJS.AES.decrypt(textEncrypt, key);
  // const textOrigin = bytes.toString(CryptoJS.enc.Utf8);

  // return textOrigin;
}

export function getFormatedPhoneNumber(phoneNumber: string) {
  if (!phoneNumber) {
    return;
  }
  //This is for check when the phone number includes +1
  if (phoneNumber.slice(0, 2) === '+1') {
    return phoneNumber
      .replace(/\D/g, '')
      .replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
  }
  return phoneNumber
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d{3})(\d{4})/, '+1 ($1) $2-$3');
}

export const getCrmListIds = crm => crm?.map(v => v?.value?.toString());

export const textAlign = (
  alignment: 'bottom' | 'left' | 'right' | 'top' | 'center',
) => {
  switch (alignment) {
    case 'left':
    case 'top':
      return 'flex-start';
    case 'right':
    case 'bottom':
      return 'flex-end';
    default:
      return 'center';
  }
};

export const alignContent = (align: 'center' | 'start' | 'end' = 'center') => {
  switch (align) {
    case 'center':
      return 'center';
    default:
      return `flex-${align}`;
  }
};

export const getContentAlign = (contentAlign?: string | null) => {
  switch (contentAlign) {
    case 'start':
    case 'left':
      return 'flex-start';
    case 'end':
    case 'right':
      return 'flex-end';
    default:
      return 'center';
  }
};

export const getContentVerticalAlign = (
  alignment?: 'top' | 'center' | 'bottom' | null,
) => {
  switch (alignment) {
    case 'top':
      return 'flex-start';
    case 'bottom':
      return 'flex-end';
    default:
      return 'center';
  }
};

type PropertyName = keyof CSSProperties;

export const getCSSpropety = <T extends PropertyName>(
  name: T,
  value: CSSProperties[T] | null,
) => {
  return value ? { [name]: value } : {};
};

export const getSlicePadding = (
  padding: 'small' | 'medium' | 'large' = 'large',
) => `padding-slice-${padding}`;

export const getFavicon = (
  iconURL: string | undefined | null,
): null | IconURL | Array<Icon> | Icons => {
  if (!iconURL) {
    return null;
  }

  return {
    icon: iconURL,
    shortcut: iconURL,
    apple: iconURL,
  };
};

export const stripHtml = html => {
  // Use a regular expression to remove all HTML tags.
  return html.replace(/<[^>]*>?/gm, '');
};

const defaultPaymentSettings: StorePaymentSettings[] = [
  {
    reservation_mode: ReservationMode.All,
    payment_type: StorePaymentType.Cash,
    stores_provider_ids: [],
  },
  {
    reservation_mode: ReservationMode.All,
    payment_type: StorePaymentType.Debit,
    stores_provider_ids: [],
  },
  {
    reservation_mode: ReservationMode.All,
    payment_type: StorePaymentType.Ach,
    stores_provider_ids: [],
  },
  {
    reservation_mode: ReservationMode.All,
    payment_type: StorePaymentType.Credit,
    stores_provider_ids: [],
  },
];

export const filterResModAndProviderStoreId = (
  s: StorePaymentSettings | StoreAgreement | Closure | null,
  providerStoreId?: string,
  res?: ReservationMode,
): boolean => {
  return !!(
    s?.stores_provider_ids?.includes(providerStoreId!) &&
    (s?.reservation_mode === res || s?.reservation_mode === ReservationMode.All)
  );
};

export const filterStoreContent = (
  store: Store,
  providerStoreId?: string,
  isPickup?: boolean | null,
  deliveryAddress?: OrderAddressInput | null,
  isExpress?: boolean,
  treezStores?: TreezStore[] | null,
): Store => {
  const res = isPickup ? ReservationMode.Pickup : ReservationMode.Delivery;

  // filter payment settings
  const payment_settings = store?.payment_settings?.filter(s =>
    filterResModAndProviderStoreId(s, providerStoreId, res),
  );

  // filter agreements
  const agreements = store?.agreements?.filter(s =>
    filterResModAndProviderStoreId(s, providerStoreId, res),
  );

  // filter closures
  const closures = store?.closures?.filter(s =>
    filterResModAndProviderStoreId(s, providerStoreId, res),
  );

  // pick up hours
  const pickup_schedules = store?.pickup_schedules?.filter(
    s => s?.stores_provider_id === providerStoreId,
  );

  const useCurbsideSchedules =
    res === ReservationMode.Pickup &&
    isCurbsideAllowedForStore(providerStoreId);

  const curbside_schedules = store?.curbside_schedules?.filter(
    s => s?.stores_provider_id === providerStoreId,
  );

  let delivery_schedules = DefaultDeliverySchedules;

  // we only need to filter this if it is a delivery order
  // becasue we are not using this info at checkout
  if (res === ReservationMode.Delivery) {
    const f = getDeliveryScheduleByAddress(
      {
        latitude: deliveryAddress?.latitude || 0,
        longitude: deliveryAddress?.longitude || 0,
        zip: deliveryAddress?.zip || '',
      },
      store,
      treezStores,
      providerStoreId,
    );

    if (f) {
      delivery_schedules = [f];
    }
  }

  let ds = delivery_schedules;

  // filter express delivery times slots if the zone
  // offers express delivery
  if ((ds[0] as ScheduleZone)?.express_menu_config?.active) {
    ds = ds?.map(ds => {
      return {
        ...ds,
        time_slots: ds.time_slots?.filter(ts =>
          isExpress ? ts?.is_express : !ts?.is_express,
        ),
      };
    });
  }

  const ps = pickup_schedules?.length
    ? pickup_schedules
    : DefaultPickupSchedules;

  const cs =
    useCurbsideSchedules && curbside_schedules?.length
      ? (curbside_schedules as CurbsideScheduleZone[])
      : undefined;

  return {
    ...store,
    delivery_schedules: ds as ScheduleZone[],
    pickup_schedules: ps as PickupScheduleZone[],
    curbside_schedules: cs,
    payment_settings: payment_settings?.length
      ? payment_settings
      : defaultPaymentSettings,
    agreements,
    closures,
  };
};

export const checkOrderForCustomDeliveryFee = (
  gcStoreWithGeoInfo: Store,
  order: OrderInput,
  store?: TreezStore,
  subtotal?: number,
) => {
  // this is a config from treez store settings that allows
  // customer to use a custom delivery fee
  const res = order?.delivery_details?.pickup
    ? ReservationMode.Pickup
    : ReservationMode.Delivery;

  if (res === ReservationMode.Delivery && store?.allow_custom_delivery_fee) {
    // can can ref delivery schedule on zero index because is gets default
    // before we call this method
    order.allow_custom_delivery_fee = true;
    order.total_delivery =
      gcStoreWithGeoInfo?.delivery_schedules?.[0]?.delivery_fee ?? 0;

    const freeAfter =
      gcStoreWithGeoInfo?.delivery_schedules?.[0]?.free_after ?? -1;

    if (freeAfter > 0) {
      if ((subtotal ?? 0) > freeAfter) {
        order.total_delivery = 0; // fee delivery
      }
    }
  }
};

/**
 * Returns the center (lat/lng) for a radius schedule from the matching TreezStore _geoloc.
 * Throws if the provider is not found in treezStores or has no _geoloc.
 */
export const getCenterForRadiusSchedule = (
  storesProviderId: string | null | undefined,
  treezStores: TreezStore[] | null | undefined,
): { lat: number; lng: number } => {
  const treezStore = treezStores?.find(t => t.short_name === storesProviderId);
  const geoloc = treezStore?.geoloc;
  if (geoloc?.lat == null || geoloc?.lng == null) {
    throw new Error(PROVIDER_GEOLOC_NOT_CONFIGURED);
  }
  return { lat: geoloc.lat, lng: geoloc.lng };
};

/**
 * Returns **all** delivery schedules whose zones (zip / geofence / radius) contain the address.
 * Does **not** fall back to {@link DefaultDeliverySchedules}.
 */
export const findMatchingDeliverySchedulesForAddress = (
  address: ILocalAddress,
  store: Store,
  treezStores?: TreezStore[] | null,
): ScheduleZone[] => {
  const matchesAddress = (
    schedule: Maybe<ScheduleZone>,
  ): schedule is ScheduleZone => {
    try {
      switch (schedule?.geo_type) {
        case GeoType.Zip:
          return !!schedule?.zips?.includes(address.zip as string);
        case GeoType.Geofence:
          return isPointInsidePolygon(address, schedule?.polygon_geo_zone);
        case GeoType.Radius: {
          const center = getCenterForRadiusSchedule(
            schedule?.stores_provider_id,
            treezStores,
          );
          return isPointInsideCircle(address, center, schedule?.radius ?? 0);
        }
        default:
          return false;
      }
    } catch {
      return false;
    }
  };

  return store?.delivery_schedules?.filter(matchesAddress) ?? [];
};

/**
 * Returns the delivery schedule whose zone (zip / geofence / radius) contains the address,
 * or `undefined` if none match. Does **not** fall back to {@link DefaultDeliverySchedules}
 * — use that when you need to know if the address is actually in a configured zone.
 *
 * When `providerStoreId` is provided and several zones overlap the address (delivery zone
 * routing), the schedule belonging to that store is preferred so its rules (min / fees / hours /
 * time slots) apply; otherwise the first matching zone is returned (legacy behavior).
 */
export const findMatchingDeliveryScheduleForAddress = (
  address: ILocalAddress,
  store: Store,
  treezStores?: TreezStore[] | null,
  providerStoreId?: string,
): ScheduleZone | undefined => {
  const matches = findMatchingDeliverySchedulesForAddress(
    address,
    store,
    treezStores,
  );

  if (!matches.length) return undefined;

  if (providerStoreId) {
    const scoped = matches.find(
      schedule => schedule?.stores_provider_id === providerStoreId,
    );
    if (scoped) return scoped;
  }

  return matches[0];
};

/**
 * Deduped list of provider store ids whose configured delivery zones contain the address.
 * Returns an empty array when the address is missing or matches no zone.
 */
export const getOverlappingDeliveryProviderIds = (
  address: ILocalAddress | undefined | null,
  store: Store,
  treezStores?: TreezStore[] | null,
): string[] => {
  if (!address) return [];

  return [
    ...new Set(
      findMatchingDeliverySchedulesForAddress(address, store, treezStores)
        .map(schedule => schedule?.stores_provider_id)
        .filter((id): id is string => !!id),
    ),
  ];
};

/**
 * Picks the provider id to apply for a delivery selection: keeps the current selection when it is
 * still valid for the address, otherwise falls back to the first matching zone. Returns `undefined`
 * when no zone matches.
 */
export const getDeliveryProviderId = (
  providerIds: string[],
  selectedStoreId?: string,
): string | undefined => {
  if (!providerIds.length) return undefined;

  return selectedStoreId && providerIds.includes(selectedStoreId)
    ? selectedStoreId
    : providerIds[0];
};

export const getDeliveryScheduleByAddress = (
  address: ILocalAddress,
  store: Store,
  treezStores?: TreezStore[] | null,
  providerStoreId?: string,
) => {
  const ds = findMatchingDeliveryScheduleForAddress(
    address,
    store,
    treezStores,
    providerStoreId,
  );

  return ds ?? (DefaultDeliverySchedules[0] as ScheduleZone);
};

/** True when the store has at least one delivery zone schedule (zip / geofence / radius). */
export const storeHasConfiguredDeliveryZones = (
  store: Store | null | undefined,
): boolean => (store?.delivery_schedules?.filter(Boolean).length ?? 0) > 0;

export type StoreDeliveryScheduleResolution = {
  /** When true, matching is strict (no default schedule fallback). */
  hasConfiguredZones: boolean;
  /** Schedule for the address; `undefined` if strict matching and the address is outside all zones. */
  schedule: ScheduleZone | undefined | null;
};

/**
 * Resolves which delivery schedule applies: legacy stores use {@link getDeliveryScheduleByAddress}
 * (default fallback); stores with configured zones use strict {@link findMatchingDeliveryScheduleForAddress}.
 */
export const resolveStoreDeliverySchedule = (
  address: ILocalAddress,
  store: Store,
  treezStores?: TreezStore[] | null,
  providerStoreId?: string,
): StoreDeliveryScheduleResolution => {
  if (!storeHasConfiguredDeliveryZones(store)) {
    return {
      hasConfiguredZones: false,
      schedule: getDeliveryScheduleByAddress(
        address,
        store,
        treezStores,
        providerStoreId,
      ),
    };
  }

  return {
    hasConfiguredZones: true,
    schedule: findMatchingDeliveryScheduleForAddress(
      address,
      store,
      treezStores,
      providerStoreId,
    ),
  };
};

export const isDeliveryAddressOutOfZone = (
  address: ILocalAddress | null | undefined,
  store: Store | null | undefined,
  treezStores?: TreezStore[] | null,
  providerStoreId?: string,
): boolean => {
  const canBeLocated =
    !!address?.zip || (!!address?.latitude && !!address?.longitude);

  if (!store || !canBeLocated) return false;

  const { hasConfiguredZones, schedule } = resolveStoreDeliverySchedule(
    address,
    store,
    treezStores,
    providerStoreId,
  );

  return hasConfiguredZones && !schedule;
};

const isPointInsidePolygon = (
  deliveryAddress: ILocalAddress,
  polygonGeoZone?: string | null,
) => {
  if (!polygonGeoZone) return false;

  let isInsidePolygon = false;
  try {
    const point = new google.maps.LatLng(
      deliveryAddress.latitude || 0,
      deliveryAddress.longitude || 0,
    );

    const polygon: google.maps.Polygon = new google.maps.Polygon({
      paths: JSON.parse(polygonGeoZone).map(
        ({ lat, lng }: { lat: number; lng: number }) => ({ lat, lng }),
      ),
    });

    isInsidePolygon = google.maps.geometry.poly.containsLocation(
      point,
      polygon,
    );

    return isInsidePolygon;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('invalid polygonGeoZone JSON string', error);
    return false;
  }
};

const isPointInsideCircle = (
  deliveryAddress: ILocalAddress,
  center: { lat: number; lng: number },
  radius: number,
) => {
  try {
    const point = new google.maps.LatLng(
      deliveryAddress.latitude || 0,
      deliveryAddress.longitude || 0,
    );

    const centerPoint = new google.maps.LatLng(center.lat, center.lng);

    const distance = google?.maps?.geometry?.spherical?.computeDistanceBetween(
      point,
      centerPoint,
    );

    const distanceInMiles = metersToMiles(distance);

    return distanceInMiles <= radius;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('invalid radius config', err);

    return false;
  }
};

const metersToMiles = (meters: number) => meters / 1609.344;

export const getDeliveryScheduleByZip = (zip: string, store: Store) => {
  const ds = store?.delivery_schedules?.find(schedule =>
    schedule?.zips?.includes(zip as string),
  );

  return ds;
};

export const getBogoSpecialURL = (
  d: Pick<Discount, 'discount_title' | 'discount_id'>,
): string => {
  return `${TEMPLATE_BASE_PATH.SPECIALS}${stringToSlug(
    d?.discount_title,
  )}?special_id=${d?.discount_id}`;
};

export const getBogoFromProduct = (product: GProduct): Discount | undefined =>
  product?.discounts?.find(
    item => item?.discount_method?.toUpperCase() === 'BOGO',
  );

export const getBundleFromProduct = (product: GProduct): Discount | undefined =>
  product?.discounts?.find(
    item => item?.discount_method?.toUpperCase() === 'BUNDLE',
  );

export const getTargetPriceDiscountsFromProduct = (
  product: GProduct,
): Discount[] =>
  product?.discounts?.filter(
    item => item?.discount_method?.toUpperCase() === 'TARGET_PRICE',
  );

export const formatPhoneNumber = (phone: string) => {
  const phoneNumber = phone.match(/^[^(]+/);
  return phoneNumber ? phoneNumber[0].trim() : '';
};

export const getMaxWidthForDevice = (
  currentWidth: number,
  desktopValue: number | undefined,
  mobileValue: number | undefined,
) => {
  if (currentWidth >= DESKTOP_MIN_WIDTH && desktopValue) {
    return `${desktopValue}px`;
  }

  if (currentWidth < DESKTOP_MIN_WIDTH && mobileValue) {
    return `${mobileValue}px`;
  }

  return;
};

export const getPaymentMethods = (
  store: Store | undefined,
): StorePaymentType[] => {
  const paymentTypes = store?.payment_settings
    ?.map((payment: Maybe<StorePaymentSettings>) => payment?.payment_type)
    ?.filter(Boolean) as StorePaymentType[];

  return [...new Set(paymentTypes)];
};

export const isNullish = (value: any): value is null | undefined =>
  value === null || value === undefined;

export const getActivesPreCartDiscounts = (
  cmsDiscountsItems?: PromoCarouselDocumentDataCarouselItemsItem[],
  preCartTreezDiscounts?: TreezSpecials[],
) => {
  // If no CMS items, return Treez discounts with default values
  if (!cmsDiscountsItems?.length) {
    if (!preCartTreezDiscounts?.length) return {};

    return preCartTreezDiscounts.reduce<
      Record<string, PromoCarouselDocumentDataCarouselItemsItemWithID>
    >((result, treezDiscount) => {
      return {
        ...result,
        [treezDiscount.id]: {
          brands: treezDiscount?.brands,
          categories: treezDiscount?.categories,
          id: treezDiscount?.id,
          title:
            treezDiscount?.displayTitle?.trim() || treezDiscount?.title?.trim(),
          description: treezDiscount?.description
            ? [
                {
                  type: 'paragraph',
                  text: treezDiscount.description,
                  spans: [],
                },
              ]
            : [],
          treezDiscountTitle: treezDiscount?.displayTitle,
          treezDiscountDescription: treezDiscount?.description,
          imageUrl: treezDiscount?.imageUrl,
          displayImageOnly: treezDiscount?.displayImageOnly,
          areTreezTitleAndDescriptionPresent: Boolean(
            treezDiscount.displayTitle && treezDiscount.description,
          ),
          definedInPrismic: false,
          pos_title_name: treezDiscount?.title,
          background_color: null,
          picture: treezDiscount?.imageUrl
            ? { url: treezDiscount.imageUrl, alt: treezDiscount.title }
            : null,
          cta_link: null,
          cta_text: null,
        } as PromoCarouselDocumentDataCarouselItemsItemWithID,
      };
    }, {});
  }

  const defaultCMSDiscountItem = cmsDiscountsItems?.find(
    item => item?.pos_title_name?.toLocaleLowerCase()?.trim() === 'default',
  );

  return cmsDiscountsItems?.reduce<
    Record<string, PromoCarouselDocumentDataCarouselItemsItemWithID>
  >((result, current, index) => {
    if (!current?.pos_title_name) {
      return {
        ...result,
        [`ad-${index}`]: {
          ...current,
        },
      };
    }

    const currentPostTitleName = current?.pos_title_name
      ?.toLocaleLowerCase()
      ?.trim();

    const currentCMSDiscountTitle = current?.title?.trim();

    const formatDiscount = (discount: TreezSpecials): TreezSpecials => {
      return { ...discount, method: discount?.method?.replace(/_/g, ' ') };
    };

    if (currentPostTitleName === 'default') {
      preCartTreezDiscounts?.forEach(treezDiscount => {
        if (!result?.[treezDiscount?.id]) {
          result = {
            ...result,
            [treezDiscount?.id]: {
              ...evaluate(current, formatDiscount(treezDiscount)),
              brands: treezDiscount?.brands,
              categories: treezDiscount?.categories,
              id: treezDiscount?.id,
              title:
                treezDiscount?.displayTitle?.trim() ||
                treezDiscount?.title?.trim(),
              treezDiscountTitle: treezDiscount?.displayTitle,
              treezDiscountDescription: treezDiscount?.description,
              imageUrl: treezDiscount?.imageUrl,
              displayImageOnly: treezDiscount?.displayImageOnly,
              areTreezTitleAndDescriptionPresent: Boolean(
                treezDiscount.displayTitle && treezDiscount.description,
              ),
              definedInPrismic: false,
            },
          } as {
            [key: string]: PromoCarouselDocumentDataCarouselItemsItemWithID;
          };
        }
      });

      return result;
    }

    const treezDiscount = preCartTreezDiscounts?.find(
      special =>
        special?.title?.toLocaleLowerCase().trim() === currentPostTitleName,
    );

    if (!treezDiscount) return result;

    return {
      ...result,
      [treezDiscount.id]: {
        ...evaluate(current, treezDiscount),
        brands: treezDiscount?.brands,
        categories: treezDiscount?.categories,
        id: treezDiscount?.id,
        title:
          currentCMSDiscountTitle ||
          treezDiscount?.displayTitle?.trim() ||
          treezDiscount?.title?.trim(),
        treezDiscountTitle: treezDiscount?.displayTitle,
        treezDiscountDescription: treezDiscount?.description,
        imageUrl: treezDiscount?.imageUrl,
        picture: current?.picture?.url
          ? current?.picture
          : (defaultCMSDiscountItem?.picture as ImageField),
        displayImageOnly: treezDiscount?.displayImageOnly,
        areTreezTitleAndDescriptionPresent: Boolean(
          treezDiscount.displayTitle && treezDiscount.description,
        ),
        definedInPrismic: true,
      },
    };
  }, {});
};

export const getSpecialURL = (
  special: PromoCarouselDocumentDataCarouselItemsItemWithID,
) => {
  // if id is undefined is because the special is a item without a POS Title Name
  if (special?.id) {
    return getSpecialLink(special);
  }

  return special?.cta_link ?? '/';
};

export const allowedAge = (
  customerType: EnumCustomerType,
  r?: AgeRestriction,
): number => {
  // this case will be for when there is not store selected
  if (!r) {
    return 21;
  }

  if (customerType === CustomerType.MEDICAL) {
    return r?.MEDICAL ?? 21;
  }

  return r?.ADULT ?? 21;
};

export const getTaxValue = (displayPostTax?: boolean, tax?: number) => {
  if (displayPostTax) {
    return 'Included';
  }

  if (!tax) {
    return 'Free';
  }

  return `$${currencyFormat(tax)}`;
};

export const convertSlugToTitle = (slug: string): string => {
  try {
    return slug
      .split('-')
      .map(i => capitalize(i))
      .join(' ');
  } catch (error) {
    return slug;
  }
};

export const getCartItemsSubtotal = (cartItems: CartLineItem[]) => {
  return cartItems.reduce((sum, { weightVariant: v, quantity }) => {
    const salePrice = v?.salePrice ?? 0;
    const price = v?.price ?? 0;
    return sum + (salePrice < price ? salePrice : price) * quantity;
  }, 0);
};

export const getCartItemsCount = (cartItems: CartLineItem[]) =>
  cartItems?.reduce((sum, current) => sum + (current?.quantity ?? 0), 0) ?? 0;

export const formatAddress = ({
  address,
  format = 'long',
}: {
  address?: OrderAddress;
  format: 'short' | 'long';
}) => {
  const shortFormat = [address?.address1, address?.address2, address?.city];
  const longFormat = [
    address?.address1,
    address?.address2,
    address?.city,
    address?.province,
    address?.zip,
    address?.country_code,
  ];

  if (format === 'short') return shortFormat?.filter(Boolean).join(', ') || '';

  return longFormat?.filter(Boolean).join(', ') || '';
};

export const getGoogleApiKey = (googleMapsApi?: string) => {
  if (!googleMapsApi) return '';

  try {
    const urlObj = new URL(googleMapsApi);
    return urlObj.searchParams.get('key') ?? '';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('invalid googleApiUrl', error);
    return '';
  }
};

export const getQueryByText = (
  special?: PromoCarouselDocumentDataCarouselItemsItemWithID,
  searchTerm?: string,
) =>
  [asText(special?.description), special?.pos_title_name, special?.title].some(
    text => text?.toLowerCase()?.includes(searchTerm?.toLowerCase() ?? ''),
  );

const formatNumberType = (num: number): string => {
  const truncated = Math.floor(num * 100) / 100;
  return truncated % 1 === 0
    ? `${truncated.toFixed(0)}`
    : `${truncated.toFixed(2)}`;
};

const formatValue = (value: number, amountType: string): string => {
  const suffix = amountType === 'PERCENTAGE' ? '%' : amountType;
  return `${formatNumber(value)}${suffix}`;
};

export const getCompoundDisplayValue = (
  type: Compound,
  product: GProduct,
  itShowDetailAvg: boolean = false,
): string | null => {
  if (!product) return null;

  const labParamByCompound: Partial<Record<Compound, string>> = {
    TAC: 'TOTAL_CANNABINOIDS',
    TERPS: 'TOTAL_TERPENES',
  };
  const labParam = labParamByCompound[type] ?? type;
  const labResult = product.labResults?.find(r => r?.result_type === labParam);

  if (labResult?.amount && labResult.amount_type) {
    const { minimum_value, maximum_value } = labResult.amount;
    const { amount_type } = labResult;

    const min = parseFloat(minimum_value);
    const max = parseFloat(maximum_value);

    const bothInvalid = isNaN(min) || isNaN(max);
    const bothZero = min === 0 && max === 0;
    const sameValue = min === max;

    // If itShowDetailAvg is true, show the full range
    if (itShowDetailAvg && !bothInvalid && !bothZero) {
      if (sameValue) {
        return `${formatValue(min, amount_type)}`;
      }
      return `${formatValue(min, amount_type)} - ${formatValue(
        max,
        amount_type,
      )}`;
    }

    // Show range if min and max are valid and distinct
    if (!bothInvalid && !bothZero && !sameValue) {
      const avg = (min + max) / 2;
      return `${formatValue(avg, amount_type)} AVG.`;
    }

    // Lab-only compounds on cards (no top-level OpenSearch fallback): show single lab value when min === max.
    const showSingleLabValueOnCard =
      type === 'TAC' || type === 'THCA' || type === 'TERPS';

    if (!bothInvalid && sameValue && showSingleLabValueOnCard) {
      return `${formatValue(min, amount_type)}`;
    }
  }

  // If laboratory data are unavailable (or invalid), use the product data.
  if (type !== 'TAC' && type !== 'TERPS') {
    const compound = product[type.toLowerCase() as 'thc' | 'cbd' | 'thca'];
    if (compound?.percentage) {
      return `${formatNumberType(compound.percentage)}%`;
    }
    if (compound?.amount) {
      return `${formatNumberType(compound.amount)}MG`;
    }
  }

  return null;
};

export const formatSpecialFlowerType = (
  flowerType?: string,
  isForUrl?: boolean,
) => {
  const specialTypes = ['SI', 'IS'];

  if (!flowerType) {
    return null;
  }

  const processedInput = flowerType?.replace(/\s+/g, '')?.toUpperCase();

  if (specialTypes.includes(processedInput)) {
    if (isForUrl) {
      return processedInput;
    }
    return processedInput?.split('')?.join('/');
  }

  return flowerType;
};

export const formatSizeFilter = (name: string) => {
  const match = name.match(/^([a-zA-Z]+)_(\d+(?:\.\d+)?)$/);

  if (!match) return '';

  const [, unit, value] = match;
  return `${value}${unit.toUpperCase()}`;
};

export const normalizeSizeLabel = (label: string): string => {
  if (!label) return '';
  return label
    .trim()
    .toLowerCase()
    .replace(/,/g, '.')
    .replace(/\s+/g, ' ')
    .replace(/^(\d+(?:\.\d+)?)\s+([a-z]+)/i, '$1$2'); // join number + first word
};

const SIZE_SORT_RANK: Record<string, number> = {
  xs: 0,
  small: 1,
  medium: 2,
  large: 3,
  xl: 4,
};

const UNKNOWN_SIZE_SORT_RANK = 999;

const getPrimarySizeValue = (size?: string | string[]): string => {
  if (!size) return '';
  return (Array.isArray(size) ? size[0] : size) ?? '';
};

export const getSizeSortIndex = (size?: string | string[]): number => {
  const normalized = normalizeSizeLabel(getPrimarySizeValue(size));
  return SIZE_SORT_RANK[normalized] ?? UNKNOWN_SIZE_SORT_RANK;
};

export const compareProductSiblings = (
  a: { base_weight?: number; size?: string | string[] },
  b: { base_weight?: number; size?: string | string[] },
): number => {
  const aHasWeight = Boolean(a.base_weight);
  const bHasWeight = Boolean(b.base_weight);

  if (aHasWeight && bHasWeight) {
    return Number(a.base_weight) - Number(b.base_weight);
  }

  return getSizeSortIndex(a.size) - getSizeSortIndex(b.size);
};

export const getStateOfResidenceLabel = (
  stateOfResidence: string | undefined,
) => {
  return STATES_OF_RESIDENCE?.find(state => state.value === stateOfResidence)
    ?.label;
};

export const getReadableLabel = (url?: string | null): string => {
  if (!url) return 'External site';

  try {
    const hasProtocol = url.startsWith('http://') || url.startsWith('https://');
    const normalizedUrl = hasProtocol ? url : `https://${url}`;

    const u = new URL(normalizedUrl);
    const domain = u.hostname.replace(/^www\./, '').split('.')[0];

    return domain.charAt(0).toUpperCase() + domain.slice(1);
  } catch {
    return 'External site';
  }
};

type FormattedSchedule = {
  day: string;
  period: string;
};

/**
 * Processes an array of weekly working hours and groups consecutive days
 * that have identical open/closed schedules. For example, if Monday-Friday
 * all have the hours "9:00 AM - 5:00 PM", they will be condensed into a
 * single entry: { day: "Mon - Fri", period: "9:00 AM - 5:00 PM" }.
 *
 * @param {ScheduleWorkingHours[] | undefined} schedules The raw array of schedule objects.
 * @returns {FormattedSchedule[]} A condensed array of formatted schedule strings.
 */

export const getStoreScheduleInfo = (
  schedules?: ScheduleWorkingHours[],
): FormattedSchedule[] => {
  if (!schedules?.length) return [];

  const daysOfWeek: { long: WeekDay; short: string }[] = [
    { long: WeekDay.Monday, short: 'Mon' },
    { long: WeekDay.Tuesday, short: 'Tue' },
    { long: WeekDay.Wednesday, short: 'Wed' },
    { long: WeekDay.Thursday, short: 'Thu' },
    { long: WeekDay.Friday, short: 'Fri' },
    { long: WeekDay.Saturday, short: 'Sat' },
    { long: WeekDay.Sunday, short: 'Sun' },
  ];

  const scheduleMap = schedules.reduce((map, schedule) => {
    if (schedule.day_of_the_week) {
      map.set(schedule.day_of_the_week, schedule);
    }
    return map;
  }, new Map<WeekDay, ScheduleWorkingHours>());

  const processedWeek = daysOfWeek.map(dayInfo => {
    const schedule = scheduleMap.get(dayInfo.long);
    const period = formatWorkingHoursPeriod(schedule);
    return { short: dayInfo.short, period };
  });

  const result: FormattedSchedule[] = [];
  for (let i = 0; i < processedWeek.length; i++) {
    const currentDay = processedWeek[i];
    let endGroupIndex = i;

    for (let j = i + 1; j < processedWeek.length; j++) {
      if (processedWeek[j].period === currentDay.period) {
        endGroupIndex = j;
      } else {
        break;
      }
    }

    const dayString =
      i === endGroupIndex
        ? currentDay.short
        : `${currentDay.short} - ${processedWeek[endGroupIndex].short}`;

    result.push({ day: dayString, period: currentDay.period });
    i = endGroupIndex;
  }

  return result;
};

const DAY_OF_WEEK_MAP = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

// Takes a schedule object and returns a formatted time range string (e.g., "9:00 AM - 5:00 PM").
// Returns 'Closed' if the hours are not provided.
export const formatWorkingHoursPeriod = (
  schedule: ScheduleWorkingHours | undefined,
): string => {
  if (!schedule?.start || !schedule.end) {
    return 'Closed';
  }

  try {
    const startDate = parse(schedule.start, 'HH:mm', new Date());
    const endDate = parse(schedule.end, 'HH:mm', new Date());
    return `${format(startDate, 'h:mm a')} - ${format(endDate, 'h:mm a')}`;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error formatting date:', error);
    return 'Invalid Time';
  }
};

export const getTodayWorkingHoursPeriod = (
  activeWorkingHours?: ScheduleWorkingHours[] | null,
  timeZone?: string,
): string | null => {
  if (!activeWorkingHours?.length) return null;

  // Resolve "today" in the store's timezone (falls back to the viewer's day)
  const todaysDayString = timeZone
    ? new Date()
        .toLocaleDateString('en-US', { timeZone, weekday: 'long' })
        .toLowerCase()
    : DAY_OF_WEEK_MAP[new Date().getDay()];
  const todaysSchedule = activeWorkingHours.find(
    schedule => schedule?.day_of_the_week?.toLowerCase() === todaysDayString,
  );

  return formatWorkingHoursPeriod(todaysSchedule);
};

// Robust function to extract RGB from any valid CSS value
export const parseRGB = (color: string): [number, number, number] | null => {
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = color;
  const computed = ctx.fillStyle;

  const match = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return null;

  return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
};

export const aspectRatioToPaddingTop = (
  ratioStr: string,
): string | undefined => {
  const overrides: Record<string, string> = {
    '4:3': '76.27',
    '1:1': '100',
  };

  if (!ratioStr || ratioStr === 'default') {
    return undefined;
  }

  if (ratioStr in overrides) {
    return `${overrides[ratioStr]}%`;
  }

  const [w, h] = ratioStr.split(':').map(Number);

  if (isNaN(w) || isNaN(h) || w === 0) {
    throw new Error(`Aspect ratio inválido: ${ratioStr}`);
  }

  const result = (h / w) * 100;
  return `${result.toFixed(2)}%`;
};

type ImageWithDimensions = {
  dimensions?: {
    width?: number;
    height?: number;
  } | null;
};

export const getPaddingTopFromImageDimensions = (
  image?: ImageWithDimensions | null,
  adjustAspectRatio?: boolean,
): string | undefined => {
  if (
    adjustAspectRatio &&
    image?.dimensions?.width &&
    image?.dimensions?.height
  ) {
    const aspectRatio = image.dimensions.height / image.dimensions.width;
    return `${aspectRatio * 100}%`;
  }

  return undefined;
};

export const getFigurePaddingTopWithMaxHeight = (
  image?: ImageWithDimensions | null,
  adjustAspectRatio?: boolean,
  maxHeight?: number | null,
): string | undefined => {
  const paddingTop = getPaddingTopFromImageDimensions(image, adjustAspectRatio);

  if (!paddingTop) {
    return undefined;
  }

  if (maxHeight) {
    return `min(${maxHeight}px, ${paddingTop})`;
  }

  return paddingTop;
};

// Dimendions to aspectRatio
export const gcd = (a: number, b: number): number => {
  a = Math.trunc(Math.abs(a));
  b = Math.trunc(Math.abs(b));
  while (b !== 0) [a, b] = [b, a % b];
  return a || 1; // avoid returning 0
};

export const dimensionsToAspectRatio = (width: number, height: number) => {
  const g = gcd(width, height);
  return `${width / g}/${height / g}`;
};

//Merge the external and internal stores with an attribute that differentiates them: externalStore
export type TreezAndExternalStore = TreezStore & {
  externalStore?: boolean;
};

export const mergeStoresWithFlag = (
  stores: TreezStore[] = [],
  externalStores: TreezStore[] = [],
): TreezAndExternalStore[] => {
  const externalWithFlag = externalStores.map(store => ({
    ...store,
    externalStore: true,
  }));

  return [...stores, ...externalWithFlag];
};

export const sanitizeTreezSearchParams = ({
  searchParams,
}: {
  searchParams: Record<string, string | string[] | boolean[]>;
}) => {
  const filterAttributes = Object.values(PRODUCT_FILTER_ATTRIBUTES);
  // this will return an array in the format of [["brand", "Stiiizy" ], ["category", "FLOWER,EDIBLE"]]
  // then we map the keys to the correct attribute name if needed (brand -> brand.keyword)
  const mappedSearchParamsEntries = Object.entries(searchParams).map(
    ([key, value]) => {
      const mappedKey = FilterAttributeNameMapping[key] ?? key;
      return [mappedKey, value] as [
        PRODUCT_FILTER_ATTRIBUTES,
        string | string[] | boolean[],
      ];
    },
  );
  // we filter the entries to only have the valid filter attributes
  const validSearchParamsEntries = mappedSearchParamsEntries.filter(([key]) =>
    filterAttributes.includes(key),
  );

  return Object.fromEntries(validSearchParamsEntries);
};

export const getSortedCannabinoidsCompounds = (compound: LabResult[]) =>
  compound?.sort((a, b) => {
    const valueA = parseFloat(a?.amount?.maximum_value);
    const valueB = parseFloat(b?.amount?.maximum_value);

    if (isNaN(valueA) || isNaN(valueB)) {
      return 0;
    }

    return valueB - valueA;
  }) || compound;

/**
 * Generic helper for "any" headless CMS document that has slices.
 * - Preserves the type of the input document (TDoc).
 * - Ensures that all slices have the same type (TSlice).
 * - If you pass `sort`, it adds/updates `data.sort_products_on_page`.
 */
export function resolveDocument<TDoc extends PageDocumentSlice>(
  doc: TDoc,
  slices: SliceZone<IPrismicSlice>,
  sort?: SortOptionalValue,
  bottomSlices?: SliceZone<IPrismicSlice>,
): TDoc {
  const next: TDoc = {
    ...doc,
    data: {
      ...doc.data,
      slices,
      ...(sort !== undefined ? { sort_products_on_page: sort } : {}),
      bottomSlices,
    },
  };
  return next;
}

/**
 * Builds a stable string key that identifies the current listing (route + filters, excluding page).
 * Used to detect "new listing" vs "same listing, other page" for scroll-to-top behavior.
 */
export const buildListingRouteKey = (
  pathname: string | null,
  routeParams: { slug?: string; subcategory?: string } | undefined,
  searchParams: Iterable<[string, string]>,
  excludeSearchKey: string,
): string => {
  const slug = (routeParams?.slug as string) ?? '';
  const subcategory = (routeParams?.subcategory as string) ?? '';
  const searchKey = [...searchParams]
    .filter(([paramKey]) => paramKey !== excludeSearchKey)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([paramKey, paramValue]) => `${paramKey}=${paramValue}`)
    .join('&');
  return [pathname ?? '', slug, subcategory, searchKey].join('|');
};

// Converts a slug to a human readable title
export const humanizeSlug = (slug: string) => {
  if (!slug) return 'Content Page';

  return slug
    .replace(/[-_]+/g, ' ') // hyphens to spaces
    .trim()
    .replace(/\s+/g, ' ') // collapse spaces
    .replace(/^./, c => c.toUpperCase()); // capitalize initial letter
};

// Normalize the path to remove trailing slashes and ensure it starts with a slash
export const normalizePath = (href: string): string => {
  if (!href) return '/';
  let path: string;
  try {
    path = new URL(href, 'http://b').pathname;
  } catch {
    path = href.split('?')[0].split('#')[0];
  }
  return path.replace(/\/+$/, '') || '/';
};
